import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { MarketingCard } from '@/components/marketing/MarketingCard';
import { AnimatedCounter } from './AnimatedCounter';
import { fadeInScale, HERO_ANIMATION_SEQUENCE, ANIMATION_TIMING } from '@/lib/animations';

interface WeekData {
  week: number;
  amount: number;
  confidence: number;
  color: 'current' | 'blue' | 'highlight' | 'muted';
}

export const EnhancedDashboardIllustration: React.FC = () => {
  const prefersReducedMotion = useReducedMotion();
  const [animationPhase, setAnimationPhase] = useState(0);
  const [visibleWeeks, setVisibleWeeks] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingProgress, setProcessingProgress] = useState(0);

  const weeklyData: WeekData[] = [
    { week: 1, amount: 12847, confidence: 95, color: 'current' },
    { week: 2, amount: 10932, confidence: 88, color: 'blue' },
    { week: 3, amount: 14235, confidence: 92, color: 'highlight' },
    { week: 4, amount: 9847, confidence: 85, color: 'muted' },
  ];

  const maxAmount = Math.max(...weeklyData.map(w => w.amount));

  // Calculate next Tuesday dynamically
  const getNextTuesday = () => {
    const today = new Date();
    const daysUntilTuesday = (2 - today.getDay() + 7) % 7 || 7; // Tuesday is day 2
    const nextTuesday = new Date(today);
    nextTuesday.setDate(today.getDate() + daysUntilTuesday);
    
    const options: Intl.DateTimeFormatOptions = { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric' 
    };
    return nextTuesday.toLocaleDateString('en-US', options);
  };

  useEffect(() => {
    if (prefersReducedMotion) {
      setVisibleWeeks(4);
      setAnimationPhase(4);
      setUploadProgress(100);
      setProcessingProgress(100);
      return;
    }

    const baseDelay = HERO_ANIMATION_SEQUENCE.illustration.delay;
    
    // Sync with hero text: headline starts at 0.07s, typing takes ~2s, so our sequence starts around 2.5s
    const heroTextDuration = 2.5; // Time for "Stop Guessing Your Tuesday Lottery Payments"
    const syncedDelay = baseDelay + heroTextDuration;
    
    // Phase 1: File upload simulation (starts after hero text completes)
    setTimeout(() => {
      setAnimationPhase(1);
      
      // Simulate upload progress
      let progress = 0;
      const uploadInterval = setInterval(() => {
        progress += Math.random() * 15 + 10;
        if (progress >= 100) {
          progress = 100;
          clearInterval(uploadInterval);
          
          // Phase 2: File processing (1.5s after upload starts)
          setTimeout(() => {
            setAnimationPhase(2);
            
            let processProgress = 0;
            const processInterval = setInterval(() => {
              processProgress += Math.random() * 12 + 8;
              if (processProgress >= 100) {
                processProgress = 100;
                clearInterval(processInterval);
                
                // Phase 3: Results ready, show dashboard (2s after processing starts)
                setTimeout(() => {
                  setAnimationPhase(3);
                  
                  // Phase 4: Stagger week visibility (0.5s after dashboard appears)
                  setTimeout(() => {
                    setAnimationPhase(4);
                    weeklyData.forEach((_, index) => {
                      setTimeout(() => {
                        setVisibleWeeks(prev => Math.max(prev, index + 1));
                      }, index * 200);
                    });
                  }, 500);
                }, 200);
              }
              setProcessingProgress(processProgress);
            }, 100);
          }, 1500);
        }
        setUploadProgress(progress);
      }, 80);
    }, syncedDelay * 1000);
  }, [prefersReducedMotion]);

  const mainCardVariants = {
    initial: { 
      scale: 0.9, 
      opacity: 0, 
      y: 20,
      rotateX: 5
    },
    animate: { 
      scale: 1, 
      opacity: 1, 
      y: 0,
      rotateX: 0,
      transition: {
        duration: ANIMATION_TIMING.elaborate,
        ease: 'backOut',
      }
    }
  };

  const weekBarVariants = {
    initial: { scaleY: 0, opacity: 0 },
    animate: (custom: { delay: number; height: number }) => ({
      scaleY: 1,
      opacity: 1,
      transition: {
        duration: 0.8,
        delay: custom.delay,
        ease: 'backOut',
      }
    })
  };

  const confidenceRingVariants = {
    initial: { pathLength: 0, opacity: 0 },
    animate: {
      pathLength: 1,
      opacity: 1,
      transition: {
        duration: 1.5,
        delay: 1.2,
        ease: 'easeOut',
      }
    }
  };

  const floatingCardVariants = {
    initial: { scale: 0, opacity: 0, y: 20 },
    animate: (delay: number) => ({
      scale: 1,
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay,
        ease: 'backOut',
      }
    }),
    floating: {
      y: [-2, 2, -2],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut',
      }
    }
  };

  const getBarColor = (color: WeekData['color']) => {
    switch (color) {
      case 'current': return 'bg-gradient-to-t from-blue-600 to-blue-500 shadow-blue-200';
      case 'blue': return 'bg-gradient-to-t from-blue-500 to-blue-400 shadow-blue-100';
      case 'highlight': return 'bg-gradient-to-t from-emerald-600 to-emerald-500 shadow-emerald-200';
      case 'muted': return 'bg-gradient-to-t from-slate-500 to-slate-400 shadow-slate-100';
      default: return 'bg-gradient-to-t from-blue-500 to-blue-400';
    }
  };

  return (
    <div
      className="relative mx-auto max-w-md lg:max-w-lg"
      style={{ minHeight: '420px', height: '420px' }}
    >
      {/* Fixed Container to prevent size jumping */}
      <div className="relative w-full" style={{ minHeight: '420px', height: '420px' }}>
      {/* File Upload Phase */}
      <AnimatePresence>
        {animationPhase === 1 && !prefersReducedMotion && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.5 }}
          >
            <MarketingCard
              variant="default"
              hover={false}
              className="p-8 bg-gradient-to-br from-white via-gray-50/80 to-white backdrop-blur-sm border border-gray-200/60 shadow-2xl text-center"
            >
              <div className="space-y-6">
                {/* Upload Icon */}
                <motion.div
                  className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </motion.div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Uploading Lottery Data</h3>
                  <p className="text-sm text-gray-600">Processing your ticket information...</p>
                </div>
                
                {/* Upload Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <motion.div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{ width: `${uploadProgress}%` }}
                    transition={{ duration: 0.1 }}
                  />
                </div>
                <div className="text-sm text-gray-600">{Math.round(uploadProgress)}% uploaded</div>
              </div>
            </MarketingCard>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Processing Phase */}
      <AnimatePresence>
        {animationPhase === 2 && !prefersReducedMotion && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.5 }}
          >
            <MarketingCard
              variant="default"
              hover={false}
              className="p-8 bg-gradient-to-br from-white via-gray-50/80 to-white backdrop-blur-sm border border-gray-200/60 shadow-2xl text-center"
            >
              <div className="space-y-6">
                {/* Processing Icon */}
                <motion.div
                  className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                >
                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </motion.div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">AI Processing</h3>
                  <p className="text-sm text-gray-600">Analyzing patterns and generating predictions...</p>
                </div>
                
                {/* Processing Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <motion.div
                    className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full"
                    style={{ width: `${processingProgress}%` }}
                    transition={{ duration: 0.1 }}
                  />
                </div>
                <div className="text-sm text-gray-600">{Math.round(processingProgress)}% processed</div>
                
                {/* Processing Steps */}
                <div className="grid grid-cols-3 gap-2 text-xs text-gray-500">
                  <div className={`p-2 rounded ${processingProgress > 30 ? 'bg-green-100 text-green-700' : 'bg-gray-100'}`}>
                    Pattern Analysis
                  </div>
                  <div className={`p-2 rounded ${processingProgress > 60 ? 'bg-green-100 text-green-700' : 'bg-gray-100'}`}>
                    Prediction Model
                  </div>
                  <div className={`p-2 rounded ${processingProgress > 90 ? 'bg-green-100 text-green-700' : 'bg-gray-100'}`}>
                    Results Ready
                  </div>
                </div>
              </div>
            </MarketingCard>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Dashboard Card */}
      <AnimatePresence>
        {(animationPhase >= 3 || prefersReducedMotion) && (
          <motion.div
            className="absolute inset-0"
            variants={mainCardVariants}
            initial={prefersReducedMotion ? false : 'initial'}
            animate={prefersReducedMotion ? false : 'animate'}
            style={{ perspective: '1000px' }}
          >
        <MarketingCard
          variant="default"
          hover={false}
          className="p-6 bg-gradient-to-br from-white via-gray-50/80 to-white backdrop-blur-sm border border-gray-200/60 shadow-2xl"
        >
          {/* Header Section */}
          <div className="mb-6">
            <div className="bg-gradient-to-r from-gray-100 to-gray-50 rounded-lg p-4 mb-4">
              <h3 className="text-2xl font-bold text-gray-900 mb-1">Next Payment</h3>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-gray-900">
                    ${weeklyData[0].amount.toLocaleString()}
                  </div>
                  <p className="text-sm text-gray-600">Due {getNextTuesday()}</p>
                </div>
                <div className="flex items-center space-x-2">
                  {/* Confidence Ring */}
                  <div className="relative w-16 h-16">
                    <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                      <circle cx="18" cy="18" r="16" fill="none" stroke="#e5e7eb" strokeWidth="2"/>
                      <motion.circle
                        cx="18" cy="18" r="16"
                        fill="none"
                        stroke="url(#confidenceGradient)"
                        strokeWidth="2"
                        strokeDasharray="100"
                        strokeDashoffset={prefersReducedMotion ? 5 : 100}
                        variants={confidenceRingVariants}
                        initial={prefersReducedMotion ? false : 'initial'}
                        animate={prefersReducedMotion ? false : 'animate'}
                        strokeLinecap="round"
                      />
                      <defs>
                        <linearGradient id="confidenceGradient" gradientUnits="userSpaceOnUse">
                          <stop offset="0%" stopColor="#10b981"/>
                          <stop offset="100%" stopColor="#059669"/>
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xs font-bold text-green-600">
                        {weeklyData[0].confidence}%
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-green-600 font-semibold">
                    Confidence
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Weekly Chart */}
          <div className="space-y-4">
            {weeklyData.map((week, index) => (
              <AnimatePresence key={week.week}>
                {(animationPhase >= 4 ? visibleWeeks > index : animationPhase >= 3) && (
                  <motion.div
                    className="flex items-center justify-between py-2"
                    initial={prefersReducedMotion ? false : { opacity: 0, x: -20 }}
                    animate={prefersReducedMotion ? false : { opacity: 1, x: 0 }}
                    transition={{ delay: animationPhase >= 4 ? index * 0.2 : 0, duration: 0.5 }}
                  >
                    <div className="flex items-center space-x-4 flex-1">
                      <span className="text-sm font-medium text-gray-700 w-16 whitespace-nowrap">
                        Week {week.week}
                      </span>
                      
                      {/* Progress Bar */}
                      <div className="flex-1 relative">
                        <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                          <motion.div
                            className={`h-full rounded-full ${getBarColor(week.color)}`}
                            variants={weekBarVariants}
                            initial={prefersReducedMotion ? false : 'initial'}
                            animate={prefersReducedMotion ? false : 'animate'}
                            custom={{ 
                              delay: 1.0 + index * 0.2, 
                              height: (week.amount / maxAmount) * 100 
                            }}
                            style={{ 
                              width: `${(week.amount / maxAmount) * 100}%`,
                              originY: 1 
                            }}
                          />
                        </div>
                      </div>
                      
                      <div className="text-right min-w-0">
                        <div className="text-sm font-bold text-gray-900">
                          ${week.amount.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            ))}
          </div>
        </MarketingCard>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Eccentric Geometric Shapes */}
      <AnimatePresence>
        {(animationPhase >= 3 || prefersReducedMotion) && (
          <>
            {/* Top Right Rectangle */}
            <motion.div
              className="absolute -top-2 -right-2 w-16 h-10 bg-gradient-to-br from-blue-500/20 to-emerald-500/20 backdrop-blur-sm border border-blue-300/30 shadow-lg"
              style={{ transform: 'rotate(12deg)' }}
              variants={floatingCardVariants}
              initial={prefersReducedMotion ? false : 'initial'}
              animate={prefersReducedMotion ? false : ['animate', 'floating']}
              custom={2.0}
            >
              <div className="absolute inset-1 bg-gradient-to-br from-white/40 to-transparent rounded-sm" />
            </motion.div>

            {/* Bottom Left Square */}
            <motion.div
              className="absolute -bottom-3 -left-3 w-12 h-12 bg-gradient-to-br from-emerald-500/25 to-blue-500/25 backdrop-blur-sm border border-emerald-300/30 shadow-lg rounded-sm"
              style={{ transform: 'rotate(-15deg)' }}
              variants={floatingCardVariants}
              initial={prefersReducedMotion ? false : 'initial'}
              animate={prefersReducedMotion ? false : ['animate', 'floating']}
              custom={2.3}
            >
              <div className="absolute inset-1 bg-gradient-to-br from-white/30 to-transparent rounded-sm" />
              <div className="absolute top-1 right-1 w-2 h-2 bg-emerald-400/60 rounded-full" />
            </motion.div>

            {/* Top Left Small Rectangle */}
            <motion.div
              className="absolute -top-1 -left-4 w-8 h-14 bg-gradient-to-br from-slate-400/20 to-blue-400/20 backdrop-blur-sm border border-slate-300/30 shadow-md rounded-sm"
              style={{ transform: 'rotate(8deg)' }}
              variants={floatingCardVariants}
              initial={prefersReducedMotion ? false : 'initial'}
              animate={prefersReducedMotion ? false : ['animate', 'floating']}
              custom={1.8}
            >
              <div className="absolute inset-1 bg-gradient-to-br from-white/20 to-transparent rounded-sm" />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Data Points Animation */}
      {animationPhase >= 3 && !prefersReducedMotion && (
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1.5 h-1.5 bg-blue-400 rounded-full"
              style={{
                left: `${20 + Math.random() * 60}%`,
                top: `${20 + Math.random() * 60}%`,
              }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{
                scale: [0, 1.5, 1],
                opacity: [0, 1, 0.7],
              }}
              transition={{
                duration: 1.2,
                delay: 2.5 + i * 0.1,
                ease: 'backOut',
              }}
            />
          ))}
        </div>
      )}
      </div>
    </div>
  );
};